<template lang="pug">
ValidationObserver(ref="confirmationObserver")
  AppDialog.vue-time-entries-unlock-dialog(
    :title="title"
    append-to-body
    :show="show"
    @close="close(false)"
  )
    p Please review the warnings and check the boxes to confirm that you want to make these changes
    .form-group.time-entry-lock(
      v-for="(records, key) in (timeEntry.associatedRecords || affectedRecords)"
      :key="key"
    )
      div
        .lock-content.d-flex.justify-content-left.align-items-start.flex-row
          i.hi.hi-important.text-danger
          .ml-5
            span(v-html="sentence(key)")
            ul.mt-10
              li(
                v-for="(record, index) in records"
                :key="`${record.url}-${index}`"
              )
                a(
                  :href="record.url"
                  target="_blank"
                  rel="noopener noreferrer"
                )
                  | {{ recordName(key, record) }}
        .lock-confirmation
          ValidationProvider(
            v-slot="{ errors }"
            :rules="{ required: { allowFalse: false } }"
            :name="key"
            :model-value="associated[key]"
            slim
          )
            span(:class="{ 'has-error': errors[0] }")
              AppCheckbox(
                v-model="associated[key]"
                :identifier="`${type}_${key}_${dialogId}`"
              )
                template(v-if="key === 'timesheets'")
                  | I understand that modifying {{ sentenceConfirmation(key) }}
                template(v-else)
                  | I understand that editing {{ sentenceConfirmation(key) }}
              span.help-block.mb-0.ml-20.pl-5(v-if="errors[0]") It is required
    template(#footer)
      button.btn.btn-default(@click="close(false)") Cancel
      button.btn.btn-danger(
        :disabled="saving || !validBtn"
        @click="save"
      ) {{ saveBtnText }}
</template>

<script>
import { isEmpty } from 'lodash-es';
import { formatDatetimeRange } from '~/utils/time_utils';

const CONFIRMATION_SENTENCE = {
  team_payments: 'this time will remove the team member from the payment(s).',
  invoices: 'or removing this time will result in out of sync invoices(s).',
  user_invoices: 'or removing this time will result in out of sync invoices(s).',
  timesheets: "this timesheet will set the timesheet status back to 'open'. The timesheet will need to be re-approved.",
  integrations: 'or removing this time will result in out of sync data.',
  pm_integrations: 'or removing this time will result in out of sync data.',
  te_integrations: 'or removing this time will result in out of sync data.',
};
const SENTENCE = {
  team_payments: 'This time has been <strong>marked as paid</strong> by the following payment(s):',
  invoices: 'This time has been <strong>invoiced</strong> by the following invoice(s):',
  user_invoices: 'This time has been <strong>user invoiced</strong> by the following invoice(s):',
  timesheets: 'This time has been <strong>approved</strong> in the following timesheet(s):',
  integrations: "This time has been <strong>sync'ed</strong> by the following integrations(s):",
  pm_integrations: "This time has been <strong>sync'ed</strong> by the following integrations(s):",
  te_integrations: "This time has been <strong>sync'ed</strong> by the following integrations(s):",
};

export default {
  name: 'TimeEntriesOverrideDialog',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'edit',
    },
    timeEntry: {
      type: Object,
      default: () => ({}),
    },
    affectedRecords: {
      type: Object,
    },
  },
  emits: ['close', 'submit', 'update:show'],
  data() {
    return {
      saving: false,
      associated: {},
      dialogId: `dialog_${Date.now()}`,
    };
  },
  computed: {
    title() {
      return `${this.$filters.capitalize(this.type)} time confirmation`;
    },
    saveBtnText() {
      if (this.saving) {
        return 'Saving...';
      }

      return 'Confirm';
    },
    validBtn() {
      if (isEmpty(this.associated)) {
        return false;
      }

      return Object.values(this.associated).every((value) => value);
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.associated = {};
        Object.keys(this.timeEntry.associatedRecords || this.affectedRecords).forEach((key) => {
          this.associated[key] = false;
        });
      }
    },
  },
  methods: {
    async save() {
      const isValid = await this.$refs.confirmationObserver.validate();

      if (isValid) {
        this.close(true);
        this.$emit('submit');
      }
    },
    close(save = false) {
      this.$refs.confirmationObserver.reset();
      this.associated = {};
      this.$emit('update:show', false);
      if (!save) {
        this.$emit('close');
      }
    },
    formattedRange(start, stop) {
      return formatDatetimeRange(moment(start), moment(stop), { style: 'date' });
    },
    sentence(key) {
      const sentence = SENTENCE[key] || 'Unknown';

      return this.multiple ? sentence.replace('This time', 'These times') : sentence;
    },
    recordName(key, record) {
      switch (key) {
        case 'team_payments':
          return `Payment for ${this.formattedRange(record.start_date, record.stop_date)}`;
        case 'invoices':
          return record.number;
        case 'user_invoices':
          return record.number;
        case 'timesheets':
          return `${record.name} (${this.formattedRange(record.start_date, record.stop_date)})`;
        case 'integrations':
          return record.name;
        default:
          return 'Unknown';
      }
    },
    sentenceConfirmation(key) {
      const sentence = CONFIRMATION_SENTENCE[key] || 'Unknown';

      return this.multiple ? sentence.replace('this time', 'these times') : sentence;
    },
  },
};
</script>
